import React from "react";
import { Link, Typography } from "@mui/material";
import LegalPageLayout, { LegalSection } from "../components/LegalPageLayout";

const PrivacyPolicyPage: React.FC = () => (
  <LegalPageLayout
    title="Privacy Policy"
    description="This policy explains what Ray Jones collects through raymondjones.dev, why it is collected, how it is used, and the choices available to you."
  >
    <LegalSection title="1. Scope and who controls your information">
      <Typography component="p">
        This Privacy Policy applies when you visit raymondjones.dev, submit or begin
        an application, schedule a call, purchase a product or service, join a
        community, or otherwise communicate with Ray Jones (“Ray,” “we,” “us,” or
        “our”). Ray determines the purposes for which this information is used.
      </Typography>
    </LegalSection>

    <LegalSection title="2. Notice at collection: information we collect">
      <Typography component="p">Depending on how you interact with the site, we may collect:</Typography>
      <ul>
        <li>
          <strong>Contact and identity information:</strong> name, email address,
          optional phone number, LinkedIn profile, and communication preferences.
        </li>
        <li>
          <strong>Application and professional information:</strong> work experience,
          location, work authorization, interview preparation, current situation,
          goals, investment readiness, eligibility answers, application status, and
          notes created while reviewing your application.
        </li>
        <li>
          <strong>Scheduling and transaction information:</strong> booking details,
          calendar responses, payment status, purchased product or service, and
          transaction identifiers. Payment-card details are handled by the payment
          processor and are not stored on this site.
        </li>
        <li>
          <strong>Technical and usage information:</strong> IP address, browser and
          device information, referring page, pages viewed, events, timestamps,
          approximate location derived from IP, and security or diagnostic logs.
        </li>
      </ul>
    </LegalSection>

    <LegalSection title="3. How we collect information">
      <Typography component="p">
        We collect information directly from you, automatically through site
        technologies, and from services you choose to use with the site—such as
        Cal.com for scheduling, Stripe for payments, Skool for community access, and
        social or advertising platforms that refer you to the site.
      </Typography>
    </LegalSection>

    <LegalSection title="4. Why we use information">
      <ul>
        <li>To evaluate applications, determine program fit, and recommend next steps.</li>
        <li>To save, review, and follow up on submitted applications.</li>
        <li>To schedule calls, deliver services and digital products, and process payments.</li>
        <li>To send application, booking, service, email, or text communications you requested or consented to receive.</li>
        <li>To operate, secure, troubleshoot, analyze, and improve the site and offerings.</li>
        <li>To measure advertising, prevent fraud and abuse, enforce agreements, and comply with law.</li>
      </ul>
      <Typography component="p">
        Where applicable law requires a legal basis, we rely on consent, steps taken
        at your request before entering a contract, performance of a contract,
        compliance with legal obligations, and legitimate interests such as operating
        and protecting the business. You may withdraw consent prospectively, but
        withdrawal does not affect processing already lawfully completed.
      </Typography>
    </LegalSection>

    <LegalSection title="5. Email and text communications">
      <Typography component="p">
        If you provide an email address or phone number and accept the application
        disclosure, Ray may contact you about your application and related services.
        Message frequency varies. Message and data rates may apply. Reply STOP to a
        text to opt out, or use an unsubscribe link where one is provided. You may
        also email Ray. Consent to promotional marketing is not a condition of
        purchase. Service, application, payment, and booking messages may still be
        sent when necessary to complete a request or provide a purchased service.
      </Typography>
    </LegalSection>

    <LegalSection title="6. How information is disclosed">
      <Typography component="p">
        We may disclose information to service providers that support the business,
        including Vercel (hosting), Airtable (application records), Resend (email),
        Cal.com (scheduling), Stripe (payments), Loom (video), Meta (advertising
        measurement), Skool (community), and professional advisers. These providers
        process information under their own terms and, where applicable, on our
        behalf.
      </Typography>
      <Typography component="p">
        We may also disclose information when required by law; to protect rights,
        safety, property, or the integrity of the services; with your direction or
        consent; or as part of a financing, merger, acquisition, reorganization, sale
        of assets, or similar business transaction.
      </Typography>
    </LegalSection>

    <LegalSection title="7. Cookies, pixels, and advertising">
      <Typography component="p">
        The site may use cookies, pixels, scripts, local storage, and session storage
        for essential functionality, security, preferences, analytics, attribution,
        and advertising measurement. For example, Meta Pixel may receive browser or
        event information. Some U.S. laws may characterize advertising-related
        disclosures as “sharing” for cross-context behavioral advertising even when
        no money changes hands. We do not sell personal information for money.
      </Typography>
      <Typography component="p">
        You may use browser controls to limit cookies and may request an opt-out of
        sale or sharing by emailing us. We will also honor legally recognized browser
        preference signals where required by applicable law and technically
        applicable.
      </Typography>
    </LegalSection>

    <LegalSection title="8. Retention">
      <Typography component="p">
        We retain personal information only for as long as reasonably necessary for
        the purposes described here. Retention depends on whether an application is
        submitted, whether you become a customer, the duration of the
        relationship, operational and security needs, consent records, applicable
        limitation periods, and tax, accounting, dispute, or legal-hold obligations.
        Information may remain temporarily in protected backups, and aggregated or
        de-identified information may be retained longer.
      </Typography>
    </LegalSection>

    <LegalSection title="9. Your privacy rights">
      <Typography component="p">
        Depending on where you live, you may have rights to know, access, correct,
        delete, restrict, object to, or receive a portable copy of personal
        information; withdraw consent; opt out of certain advertising uses; appeal a
        denied request; and receive equal service without unlawful discrimination.
        These rights are subject to verification and legal exceptions.
      </Typography>
      <Typography component="p">
        Submit a request to{" "}
        <Link href="mailto:raymond@raymondjones.dev">
          raymond@raymondjones.dev
        </Link>
        . You may use an authorized agent where permitted by law. We may request
        information needed to verify the request and the agent’s authority.
      </Typography>
    </LegalSection>

    <LegalSection title="10. Security, transfers, and third-party links">
      <Typography component="p">
        We use reasonable administrative, technical, and organizational safeguards,
        but no transmission or storage method is completely secure. Service providers
        may process information in the United States or other countries with
        different privacy rules. Third-party websites and embedded services are
        governed by their own policies, and we are not responsible for their
        independent practices.
      </Typography>
    </LegalSection>

    <LegalSection title="11. Children">
      <Typography component="p">
        The site, applications, and paid offerings are intended for adults age 18 or
        older. We do not knowingly collect personal information from children under
        13. If you believe a child provided information, contact us so we can review
        and delete it where appropriate.
      </Typography>
    </LegalSection>

    <LegalSection title="12. Changes and contact">
      <Typography component="p">
        We may update this policy as the site, business, or law changes. The updated
        version will be posted with a revised date, and additional notice will be
        provided when legally required. Questions and privacy requests may be sent
        to{" "}
        <Link href="mailto:raymond@raymondjones.dev">
          raymond@raymondjones.dev
        </Link>
        .
      </Typography>
    </LegalSection>
  </LegalPageLayout>
);

export default PrivacyPolicyPage;
